/* G-TIDE — microinteracciones premium (parallax, video de marca, zoom, carrito) */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ---------- Banner: parallax suave con el cursor ---------- */
  function initHero(hero) {
    if (hero.dataset.ready) return;
    hero.dataset.ready = 'true';
    const items = [...hero.querySelectorAll('[data-depth]')];
    if (!items.length) return;
    let frame = null;
    const move = (x, y) => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        items.forEach((item) => {
          const depth = parseFloat(item.dataset.depth) || 1;
          item.style.setProperty('--px', `${(x * 14 * depth).toFixed(2)}px`);
          item.style.setProperty('--py', `${(y * 10 * depth).toFixed(2)}px`);
        });
      });
    };
    hero.addEventListener('pointermove', (event) => {
      if (reduceMotion.matches || !finePointer.matches) return;
      const rect = hero.getBoundingClientRect();
      move((event.clientX - rect.left) / rect.width - 0.5, (event.clientY - rect.top) / rect.height - 0.5);
    });
    hero.addEventListener('pointerleave', () => move(0, 0));
  }

  /* ---------- Video de marca ---------- */
  function initFilm(frame) {
    if (frame.dataset.ready) return;
    frame.dataset.ready = 'true';
    const video = frame.querySelector('video');
    if (!video) return;
    const playButtons = frame.querySelectorAll('[data-film-toggle]');
    const muteButton = frame.querySelector('[data-film-mute]');
    let userPaused = false;

    const pickSource = () => {
      if (video.getAttribute('src')) return;
      const small = window.matchMedia('(max-width: 749px)').matches || (navigator.connection && navigator.connection.saveData);
      video.src = (small && frame.dataset.srcSd) || frame.dataset.srcHd || frame.dataset.srcSd;
    };
    const sync = () => {
      frame.classList.toggle('is-playing', !video.paused);
      frame.classList.toggle('is-unmuted', !video.muted);
      playButtons.forEach((btn) => btn.setAttribute('aria-pressed', String(!video.paused)));
      if (muteButton) muteButton.setAttribute('aria-pressed', String(!video.muted));
    };
    const play = () => {
      pickSource();
      frame.classList.add('has-started');
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => frame.classList.remove('has-started'));
    };

    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('volumechange', sync);
    playButtons.forEach((btn) =>
      btn.addEventListener('click', () => {
        if (video.paused) { userPaused = false; play(); } else { userPaused = true; video.pause(); }
      })
    );
    if (muteButton) {
      muteButton.addEventListener('click', () => {
        video.muted = !video.muted;
        if (!video.muted && video.paused) play();
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.45) {
              if (!reduceMotion.matches && !userPaused && frame.dataset.autoplay === 'true') { video.muted = true; play(); }
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.45, 0.8] }
      ).observe(frame);
    }
    sync();
  }

  /* ---------- Zoom de imágenes en la página de producto ---------- */
  let zoomDialog = null;
  function getZoom() {
    if (zoomDialog) return zoomDialog;
    zoomDialog = document.createElement('div');
    zoomDialog.className = 'gt-zoom';
    zoomDialog.setAttribute('role', 'dialog');
    zoomDialog.setAttribute('aria-modal', 'true');
    zoomDialog.setAttribute('aria-label', 'Imagen ampliada');
    zoomDialog.innerHTML =
      '<button type="button" class="gt-zoom__close" aria-label="Cerrar">' +
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
      '<div class="gt-zoom__stage"><img alt=""></div>' +
      '<p class="gt-zoom__hint">Mueve el cursor o desliza para recorrer la imagen</p>';
    document.body.appendChild(zoomDialog);
    const stage = zoomDialog.querySelector('.gt-zoom__stage');
    const img = stage.querySelector('img');
    let lastFocus = null;
    const pan = (x, y) => {
      const rect = stage.getBoundingClientRect();
      const overX = Math.max(0, img.offsetWidth - rect.width);
      const overY = Math.max(0, img.offsetHeight - rect.height);
      const px = (x / rect.width - 0.5) * overX;
      const py = (y / rect.height - 0.5) * overY;
      img.style.transform = `translate(calc(-50% - ${px}px), calc(-50% - ${py}px))`;
    };
    stage.addEventListener('pointermove', (e) => pan(e.clientX, e.clientY));
    const close = () => {
      zoomDialog.removeAttribute('open');
      document.documentElement.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    };
    stage.addEventListener('click', close);
    zoomDialog.querySelector('.gt-zoom__close').addEventListener('click', close);
    zoomDialog.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    zoomDialog.openWith = (preview, src, alt) => {
      lastFocus = document.activeElement;
      // muestra al instante la imagen ya cargada y cambia a la versión grande cuando llegue
      img.src = preview;
      const hi = new Image();
      hi.onload = () => { if (zoomDialog.hasAttribute('open')) img.src = src; };
      hi.src = src;
      img.alt = alt || '';
      img.style.transform = 'translate(-50%, -50%)';
      zoomDialog.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      zoomDialog.querySelector('.gt-zoom__close').focus();
    };
    return zoomDialog;
  }
  function largestSrc(img) {
    const set = (img.getAttribute('srcset') || '').split(',').map((s) => s.trim().split(/\s+/));
    const best = set.sort((a, b) => parseInt(b[1], 10) - parseInt(a[1], 10))[0];
    return (best && best[0]) || img.currentSrc || img.src;
  }
  function currentSlideImage(gallery) {
    const viewer = gallery.querySelector('[data-gallery-viewer]');
    const slides = [...gallery.querySelectorAll('[data-slide]')];
    if (!viewer || !slides.length) return null;
    const center = viewer.scrollLeft + viewer.clientWidth / 2;
    const slide = slides.find((s) => s.offsetLeft <= center && s.offsetLeft + s.offsetWidth > center) || slides[0];
    return slide.querySelector('img');
  }
  function initGallery(gallery) {
    if (gallery.dataset.zoomReady) return;
    gallery.dataset.zoomReady = 'true';
    const viewer = gallery.querySelector('[data-gallery-viewer]');
    if (!viewer || !gallery.querySelector('[data-slide] img')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gt-zoom-trigger';
    button.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3M11 8v6M8 11h6"/></svg>Ampliar';
    button.setAttribute('aria-label', 'Ampliar imagen');
    viewer.parentElement.style.position = 'relative';
    viewer.insertAdjacentElement('afterend', button);
    const open = () => {
      const img = currentSlideImage(gallery);
      if (img) getZoom().openWith(img.currentSrc || img.src, largestSrc(img).replace(/width=\d+/, 'width=2400'), img.alt);
    };
    button.addEventListener('click', open);
    viewer.addEventListener('dblclick', open);
  }

  /* ---------- Carrito: respuesta visual al agregar ---------- */
  document.addEventListener('cart:updated', () => {
    document.querySelectorAll('.cart-count').forEach((el) => {
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  });

  const init = (root = document) => {
    root.querySelectorAll('[data-gt-hero]').forEach(initHero);
    root.querySelectorAll('[data-gt-film]').forEach(initFilm);
    root.querySelectorAll('media-gallery').forEach(initGallery);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
